import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { BarChart2, PieChart, TrendingUp, Database, Users, Award } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/services/solutions/businessintelli.css';

const BusinessIntelligence = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  return (
    <div className="business-intelligence-page">
      {/* Hero Section */}
      <motion.div 
        className="hero-section3 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Container>
          <Row className="align-items-center min-vh-80">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.h1 
                className="display-3 fw-bold mb-4"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                Business Intelligence Solutions
              </motion.h1>
              <motion.p 
                className="lead mb-4"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                Transform your data into actionable insights. Our powerful analytics dashboards help you make strategic decisions with confidence.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <Button variant="primary" size="lg" className="me-3 mb-3">Get Started</Button>
                <Button variant="outline-light" size="lg" className="mb-3">Schedule Demo</Button>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                className="dashboard-preview"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <img 
                  src="/api/placeholder/600/400" 
                  alt="Business Intelligence Dashboard Preview" 
                  className="img-fluid rounded shadow"
                />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      {/* Features Section */}
      <section className="features-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Powerful Features</h2>
            <p className="lead text-muted">Everything you need to make data-driven decisions</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { icon: <BarChart2 size={40} />, title: "Interactive Dashboards", description: "Customizable dashboards with drag-and-drop interfaces for real-time monitoring" },
                { icon: <PieChart size={40} />, title: "Visual Analytics", description: "Transform complex data into intuitive charts and graphs" },
                { icon: <Database size={40} />, title: "Data Integration", description: "Connect to multiple data sources with our pre-built connectors" },
                { icon: <TrendingUp size={40} />, title: "Predictive Analytics", description: "Forecast trends and identify opportunities with AI-powered predictions" },
                { icon: <Users size={40} />, title: "Collaborative Tools", description: "Share insights and collaborate with your team in real-time" },
                { icon: <Award size={40} />, title: "Performance Metrics", description: "Track KPIs and metrics that matter most to your business" }
              ].map((feature, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                    className="feature-card h-100"
                  >
                    <Card className="border-0 shadow-sm h-100 hover-lift">
                      <Card.Body className="p-4 text-center">
                        <div className="feature-icon mb-3 text-primary">
                          {feature.icon}
                        </div>
                        <Card.Title className="fw-bold mb-3">{feature.title}</Card.Title>
                        <Card.Text className="text-muted">{feature.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">How It Works</h2>
            <p className="lead text-muted">From raw data to actionable insights in four simple steps</p>
          </motion.div>

          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img 
                  src="/api/placeholder/600/400" 
                  alt="How Business Intelligence Works" 
                  className="img-fluid rounded shadow"
                />
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
              >
                {[
                  { step: 1, title: "Connect Data Sources", description: "Seamlessly integrate with your existing databases, CRMs, ERPs, and cloud services.", progress: 25 },
                  { step: 2, title: "Automated Data Processing", description: "Our system cleans, transforms, and prepares your data for analysis.", progress: 50 },
                  { step: 3, title: "Visualization & Analysis", description: "Create custom dashboards and reports tailored to your business needs.", progress: 75 },
                  { step: 4, title: "Share & Collaborate", description: "Distribute insights across your organization for aligned decision making.", progress: 100 }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    variants={fadeIn}
                    className="mb-4"
                  >
                    <div className="d-flex align-items-center mb-2">
                      <div className="step-number me-3">{item.step}</div>
                      <h4 className="mb-0 fw-bold">{item.title}</h4>
                    </div>
                    <p className="text-muted ms-5 ps-2 mb-2">{item.description}</p>
                    <div className="ms-5 ps-2">
                      <ProgressBar now={item.progress} variant="primary" className="progress-thin" />
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Case Studies Section */}
      <section className="case-studies-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Success Stories</h2>
            <p className="lead text-muted">See how our clients achieved remarkable results</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { 
                  company: "TechCorp Inc.",
                  industry: "Technology",
                  result: "42% increase in operational efficiency",
                  description: "By implementing our BI solution, TechCorp gained visibility into their production bottlenecks and optimized processes."
                },
                { 
                  company: "Global Retail",
                  industry: "Retail",
                  result: "27% revenue growth in 6 months",
                  description: "Our predictive analytics helped them forecast demand accurately and optimize inventory levels across 200+ stores."
                },
                { 
                  company: "HealthPlus",
                  industry: "Healthcare",
                  result: "Reduced reporting time by 85%",
                  description: "Automated dashboards replaced manual reporting, allowing staff to focus on patient care instead of spreadsheets."
                }
              ].map((study, index) => (
                <Col lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                  >
                    <Card className="border-0 shadow case-study-card h-100">
                      <Card.Body className="p-4">
                        <div className="mb-4">
                          <span className="badge bg-light text-dark mb-2">{study.industry}</span>
                          <h4 className="fw-bold">{study.company}</h4>
                        </div>
                        <Card.Text className="mb-3">{study.description}</Card.Text>
                        <div className="result-highlight p-3 rounded">
                          <h5 className="text-primary mb-0">{study.result}</h5>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call to Action */}
      <motion.section 
        className="cta-section text-white text-center py-5"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2 className="display-5 fw-bold mb-4">Ready to transform your business with data?</h2>
              <p className="lead mb-4">Get started today and see the difference our Business Intelligence solutions can make.</p>
              <Button variant="light" size="lg" className="me-3 mb-3">Request a Demo</Button>
              <Button variant="outline-light" size="lg" className="mb-3">Contact Sales</Button>
            </Col>
          </Row>
        </Container>
      </motion.section>

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Frequently Asked Questions</h2>
            <p className="lead text-muted">Everything you need to know about our BI solutions</p>
          </motion.div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
              >
                {[
                  { 
                    question: "How long does implementation typically take?", 
                    answer: "Implementation time varies based on the complexity of your data sources and specific requirements. Most clients are up and running within 2-4 weeks. Our team works closely with you to ensure a smooth and efficient setup process." 
                  },
                  { 
                    question: "Can I integrate with my existing systems?", 
                    answer: "Yes, our BI solution is designed to integrate with most common business systems including CRMs, ERPs, marketing platforms, and custom databases. We offer pre-built connectors for popular systems and can develop custom integrations as needed." 
                  },
                  { 
                    question: "Do I need technical expertise to use the dashboards?", 
                    answer: "No technical expertise is required. Our dashboards are designed with user-friendliness in mind. We provide comprehensive training and ongoing support to ensure your team can easily access and interpret the insights." 
                  },
                  { 
                    question: "How secure is my data?", 
                    answer: "Security is our top priority. We employ bank-level encryption, secure data centers, and rigorous access controls. Our platform is compliant with major security standards including SOC 2, GDPR, and HIPAA where applicable." 
                  }
                ].map((faq, index) => (
                  <motion.div 
                    key={index}
                    variants={fadeIn}
                    className="mb-4"
                  >
                    <Card className="border-0 shadow-sm">
                      <Card.Body className="p-4">
                        <h5 className="fw-bold mb-3">{faq.question}</h5>
                        <p className="text-muted mb-0">{faq.answer}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default BusinessIntelligence;